import { memo, useEffect, useState, useCallback } from "react";
import { X, MessageCircle, User, Video, Wifi, WifiOff } from "lucide-react";
import { Profile } from "@/types/profile";
import { useAuthContext } from "@/contexts/AuthContext";
import { useMatchSync } from "@/hooks/useMatchSync";

interface MatchModalProps {
  profile: Profile;
  isOpen: boolean;
  onClose: () => void;
  onStartChat: () => void;
  onViewProfile: () => void;
  onWatchVideo?: () => void;
}

interface ConfettiPiece {
  id: number;
  x: number;
  delay: number;
  duration: number;
  color: string;
  size: number;
  type: "circle" | "rect" | "triangle";
}

const CONFETTI_COLORS = [
  "#FF6B9D", // pop pink
  "#FFD93D", // gold  
  "#6BCB77", // green
  "#4D96FF", // blue
  "#FF6B6B", // coral
  "#C9B1FF", // lavender
  "#FF9F43", // orange
];

const MatchModal = memo(({ 
  profile, 
  isOpen, 
  onClose, 
  onStartChat, 
  onViewProfile,
  onWatchVideo,
}: MatchModalProps) => {
  const { user, profile: userProfile } = useAuthContext();
  const [animationPhase, setAnimationPhase] = useState<"waiting" | "enter" | "collide" | "explode" | "reveal">("waiting");
  const [confetti, setConfetti] = useState<ConfettiPiece[]>([]);

  // Real-time sync with matched user
  const { shouldStartAnimation, isSynced, isOtherOnline } = useMatchSync(
    user?.id,
    profile.id,
    isOpen
  );

  // Generate confetti pieces
  const generateConfetti = useCallback(() => {
    const pieces: ConfettiPiece[] = Array.from({ length: 50 }, (_, i) => ({
      id: i,
      x: 30 + Math.random() * 40, // Concentrate around center
      delay: Math.random() * 0.3,
      duration: 1.5 + Math.random() * 1.5,
      color: CONFETTI_COLORS[Math.floor(Math.random() * CONFETTI_COLORS.length)],
      size: 8 + Math.random() * 8,
      type: ["circle", "rect", "triangle"][Math.floor(Math.random() * 3)] as "circle" | "rect" | "triangle",
    }));
    setConfetti(pieces);
  }, []);

  // Trigger haptic feedback on mobile
  const triggerHaptic = useCallback(() => {
    if (navigator.vibrate) {
      navigator.vibrate([50, 30, 100]); // Short burst pattern
    }
  }, []);

  // Reset animation when modal opens
  useEffect(() => {
    if (isOpen) {
      setAnimationPhase("waiting");
      setConfetti([]);
    }
  }, [isOpen]);

  // Animation sequence - triggered by sync
  useEffect(() => {
    if (!isOpen || !shouldStartAnimation) return;
    
    console.log("[MatchModal] Starting synchronized animation");
    
    // Start the animation sequence
    setAnimationPhase("enter");
    
    // Phase 1: Balloons enter and move toward each other
    const collideTimer = setTimeout(() => {
      setAnimationPhase("collide");
    }, 800);

    // Phase 2: Balloons collide and pop
    const explodeTimer = setTimeout(() => {
      setAnimationPhase("explode");
      generateConfetti();
      triggerHaptic();
    }, 1400);

    // Phase 3: Reveal the match
    const revealTimer = setTimeout(() => {
      setAnimationPhase("reveal");
    }, 1800);

    return () => {
      clearTimeout(collideTimer);
      clearTimeout(explodeTimer);
      clearTimeout(revealTimer);
    };
  }, [isOpen, shouldStartAnimation, generateConfetti, triggerHaptic]);

  if (!isOpen) return null;

  const showWatchVideo = profile.hasVideo || userProfile?.has_intro_video;
  const userPhoto = userProfile?.photo_url || "/placeholder.svg";
  const matchPhoto = profile.photos[0] || "/placeholder.svg";

  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center overflow-hidden">
      {/* Gradient background */}
      <div className="absolute inset-0 bg-gradient-to-b from-purple-600 via-pink-500 to-rose-400" />
      
      {/* Synced indicator - subtle badge showing connection status */}
      {animationPhase === "waiting" && (
        <div className="absolute top-6 left-6 flex items-center gap-2 px-3 py-1.5 rounded-full bg-white/20 backdrop-blur-sm text-white text-sm z-20">
          {isOtherOnline ? (
            <>
              <Wifi className="w-4 h-4 animate-pulse" />
              <span>Syncing...</span>
            </>
          ) : (
            <>
              <WifiOff className="w-4 h-4 opacity-60" />
              <span>Waiting...</span>
            </>
          )}
        </div>
      )}

      {/* Synced badge when both online */}
      {isSynced && animationPhase !== "waiting" && (
        <div className="absolute top-6 left-6 flex items-center gap-2 px-3 py-1.5 rounded-full bg-green-500/80 text-white text-sm z-20 animate-fade-in">
          <Wifi className="w-4 h-4" />
          <span>Synced! 🎉</span>
        </div>
      )}
      
      {/* Sparkle/star background pattern */}
      <div className="absolute inset-0 overflow-hidden">
        {[...Array(20)].map((_, i) => (
          <div
            key={i}
            className="absolute w-1 h-1 bg-white rounded-full animate-pulse"
            style={{
              left: `${Math.random() * 100}%`,
              top: `${Math.random() * 100}%`,
              animationDelay: `${Math.random() * 2}s`,
              opacity: 0.4 + Math.random() * 0.6,
            }}
          />
        ))}
      </div>

      {/* Confetti explosion */}
      {(animationPhase === "explode" || animationPhase === "reveal") && (
        <div className="absolute inset-0 pointer-events-none overflow-hidden">
          {confetti.map((piece) => (
            <div
              key={piece.id}
              className="absolute animate-confetti-fall"
              style={{
                left: `${piece.x}%`,
                top: "40%",
                animationDelay: `${piece.delay}s`,
                animationDuration: `${piece.duration}s`,
              }}
            >
              {piece.type === "circle" && (
                <div 
                  className="rounded-full"
                  style={{ width: piece.size, height: piece.size, backgroundColor: piece.color }}
                />
              )}
              {piece.type === "rect" && (
                <div 
                  className="rotate-45"
                  style={{ width: piece.size, height: piece.size * 0.6, backgroundColor: piece.color }}
                />
              )}
              {piece.type === "triangle" && (
                <div 
                  style={{
                    width: 0,
                    height: 0,
                    borderLeft: `${piece.size / 2}px solid transparent`,
                    borderRight: `${piece.size / 2}px solid transparent`,
                    borderBottom: `${piece.size}px solid ${piece.color}`,
                  }}
                />
              )}
            </div>
          ))}
        </div>
      )}

      {/* Close button */}
      <button
        onClick={onClose}
        className="absolute top-6 right-6 w-10 h-10 rounded-full bg-white/20 flex items-center justify-center text-white hover:bg-white/30 transition-colors z-20"
      >
        <X className="w-6 h-6" />
      </button>

      {/* Main content */}
      <div className="relative z-10 w-full max-w-sm mx-4 flex flex-col items-center">
        
        {/* Header text */}
        <h1 
          className={`text-4xl md:text-5xl font-bold text-white mb-8 text-center transition-all duration-500 ${
            animationPhase === "reveal" ? "opacity-100 translate-y-0" : "opacity-0 -translate-y-4"
          }`}
          style={{ textShadow: "0 2px 20px rgba(0,0,0,0.3)" }}
        >
          It's a Match!
        </h1>

        {/* Balloons container */}
        <div className="relative w-full h-64 flex items-center justify-center mb-4">
          
          {/* Pop! burst effect */}
          {(animationPhase === "explode" || animationPhase === "reveal") && (
            <div className="absolute inset-0 flex items-center justify-center pointer-events-none">
              <div className="relative">
                <div className="absolute inset-0 animate-ping bg-yellow-300 rounded-full opacity-50" style={{ width: 80, height: 80 }} />
                <div 
                  className="relative bg-gradient-to-br from-yellow-300 to-orange-400 text-white font-bold text-xl px-6 py-3 rounded-full shadow-lg animate-bounce-in z-10"
                  style={{ textShadow: "0 1px 2px rgba(0,0,0,0.2)" }}
                >
                  Pop!
                </div>
              </div>
            </div>
          )}

          {/* Left balloon (current user - blue) */}
          <div 
            className={`absolute transition-all duration-700 ease-out ${
              animationPhase === "waiting"
                ? "-translate-x-40 opacity-50 scale-90"
                : animationPhase === "enter" 
                  ? "-translate-x-32 opacity-0" 
                  : animationPhase === "collide" || animationPhase === "explode" || animationPhase === "reveal"
                    ? "translate-x-[-60px] opacity-100" 
                    : ""
            }`}
            style={{ transitionTimingFunction: "cubic-bezier(0.34, 1.56, 0.64, 1)" }}
          >
            <div className="relative">
              {/* Balloon shape */}
              <svg viewBox="0 0 100 130" className="w-28 h-36 drop-shadow-lg">
                <defs>
                  <linearGradient id="blueBalloon" x1="0%" y1="0%" x2="100%" y2="100%">
                    <stop offset="0%" stopColor="#60A5FA" />
                    <stop offset="50%" stopColor="#3B82F6" />
                    <stop offset="100%" stopColor="#1D4ED8" />
                  </linearGradient>
                </defs>
                <ellipse cx="50" cy="45" rx="45" ry="50" fill="url(#blueBalloon)" />
                {/* Balloon highlight */}
                <ellipse cx="35" cy="30" rx="12" ry="18" fill="rgba(255,255,255,0.3)" />
                {/* Balloon tie */}
                <polygon points="50,95 45,100 55,100" fill="#1D4ED8" />
                {/* String */}
                <path d="M50,100 Q45,115 50,130" stroke="#666" strokeWidth="1.5" fill="none" />
              </svg>
              
              {/* User photo inside balloon */}
              <div className="absolute top-4 left-1/2 -translate-x-1/2 w-16 h-16 rounded-full overflow-hidden border-2 border-white/50 shadow-lg">
                <img
                  src={userPhoto}
                  alt="You"
                  className="w-full h-full object-cover"
                />
              </div>
            </div>
          </div>

          {/* Right balloon (match - pink) */}
          <div 
            className={`absolute transition-all duration-700 ease-out ${
              animationPhase === "waiting"
                ? "translate-x-40 opacity-50 scale-90"
                : animationPhase === "enter" 
                  ? "translate-x-32 opacity-0" 
                  : animationPhase === "collide" || animationPhase === "explode" || animationPhase === "reveal"
                    ? "translate-x-[60px] opacity-100" 
                    : ""
            }`}
            style={{ transitionTimingFunction: "cubic-bezier(0.34, 1.56, 0.64, 1)" }}
          >
            <div className="relative">
              {/* Balloon shape */}
              <svg viewBox="0 0 100 130" className="w-28 h-36 drop-shadow-lg">
                <defs>
                  <linearGradient id="pinkBalloon" x1="0%" y1="0%" x2="100%" y2="100%">
                    <stop offset="0%" stopColor="#F472B6" />
                    <stop offset="50%" stopColor="#EC4899" />
                    <stop offset="100%" stopColor="#BE185D" />
                  </linearGradient>
                </defs>
                <ellipse cx="50" cy="45" rx="45" ry="50" fill="url(#pinkBalloon)" />
                {/* Balloon highlight */}
                <ellipse cx="35" cy="30" rx="12" ry="18" fill="rgba(255,255,255,0.3)" />
                {/* Balloon tie */}
                <polygon points="50,95 45,100 55,100" fill="#BE185D" />
                {/* String */}
                <path d="M50,100 Q55,115 50,130" stroke="#666" strokeWidth="1.5" fill="none" />
              </svg>
              
              {/* Match photo inside balloon */}
              <div className="absolute top-4 left-1/2 -translate-x-1/2 w-16 h-16 rounded-full overflow-hidden border-2 border-white/50 shadow-lg">
                <img
                  src={matchPhoto}
                  alt={profile.name}
                  className="w-full h-full object-cover"
                />
              </div>
            </div>
          </div>
        </div>

        {/* Subtext */}
        <p 
          className={`text-white/90 text-lg text-center mb-8 transition-all duration-500 delay-200 ${
            animationPhase === "reveal" ? "opacity-100 translate-y-0" : "opacity-0 translate-y-4"
          }`}
        >
          You two are connected!
        </p>

        {/* Action buttons */}
        <div 
          className={`w-full space-y-3 transition-all duration-500 delay-300 ${
            animationPhase === "reveal" ? "opacity-100 translate-y-0" : "opacity-0 translate-y-8"
          }`}
        >
          {/* Primary: Chat Now */}
          <button
            onClick={onStartChat}
            className="w-full flex items-center justify-center gap-2 py-4 rounded-full bg-gradient-to-r from-cyan-400 to-blue-500 text-white font-semibold text-lg shadow-lg hover:scale-[1.02] active:scale-[0.98] transition-transform"
          >
            <MessageCircle className="w-5 h-5" />
            Chat Now
          </button>
          
          {/* Secondary: View Profile */}
          <button
            onClick={onViewProfile}
            className="w-full flex items-center justify-center gap-2 py-4 rounded-full bg-gradient-to-r from-pink-400 to-rose-500 text-white font-semibold text-lg shadow-lg hover:scale-[1.02] active:scale-[0.98] transition-transform"
          >
            <User className="w-5 h-5" />
            View Profile
          </button>

          {/* Conditional: Watch Video */}
          {showWatchVideo && onWatchVideo && (
            <button
              onClick={onWatchVideo}
              className="w-full flex items-center justify-center gap-2 py-3 rounded-full bg-white/20 backdrop-blur-sm text-white font-medium text-base border border-white/30 hover:bg-white/30 transition-colors"
            >
              <Video className="w-4 h-4" />
              Watch Video
            </button>
          )}
        </div>
      </div>
    </div>
  );
});

MatchModal.displayName = "MatchModal";

export default MatchModal;
